<template>
  <Teleport to="body">
    <Transition name="toast">
      <div
        v-if="toastStore.toasts.length"
        class="toaster__wrapper"
        :class="{ rtl: isRtl }"
      >
        <TransitionGroup name="toast" tag="ul">
          <li
            v-for="toast in toastStore.toasts"
            :class="['toaster__inner', toastClassMap[toast.status]]"
            :key="toast.id"
          >
            <VIcon
                :icon="toastIconMap[toast.status]"
              class="toaster__inner-icon"
            />

            <span class="toaster__inner-text">
              {{ toast.text }}
            </span>

            <button
              type="button"
              class="toaster__inner-close"
              :aria-label="t('alkokh.toaster.dismiss')"
              :title="t('alkokh.toaster.dismiss')"
              @click="toastStore.dismiss(toast.id)"
            >
              <VIcon icon="tabler-x" size="20" />
            </button>
          </li>
        </TransitionGroup>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import useToasterStore, { TToastStatus } from "@/stores/toastify.store";
import { useI18n } from "vue-i18n";

const toastClassMap: Record<TToastStatus, string> = {
  warning: "warning",
  error: "error",
  success: "success",
};

const toastIconMap: Record<TToastStatus, string> = {
  error: "tabler-bug-off",
  warning: "tabler-alert-circle",
  success: "tabler-circle-check",
};
const toastStore = useToasterStore();

const { locale, t } = useI18n({ useScope: "global" });
const isRtl = computed(() => String(locale.value).toLowerCase().startsWith("ar"));
</script>

<style scoped lang="scss">
.toast-enter-from,
.toast-leave-to {
  transform: translateX(var(--toast-from-x, 100%));
  opacity: 0;
}

.toast-enter-active,
.toast-leave-active {
  transition: 0.25s ease all;
}

.toaster {
  &__wrapper {
    --toast-from-x: 100%;
    position: fixed;
    bottom: 3%;
    right: 5%;

    // Bounded so a stack of long messages scrolls rather than covering the screen.
    max-block-size: 80vh;
    overflow-y: auto;

    z-index: 6000;

    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__wrapper.rtl {
    --toast-from-x: -100%;
    left: 5%;
    right: auto;
  }

  &__inner {
    --color: rgb(var(--v-theme-on-surface));
    display: flex;
    // Top-aligned, not centred: a three-line refusal would otherwise float its icon in the
    // middle of the block. Backend messages are as long as they need to be.
    align-items: flex-start;
    gap: 1rem;

    // Room to read, and a ceiling so a long message cannot run the width of the screen.
    max-inline-size: min(46rem, calc(100vw - 4rem));

    border-radius: 0.3rem;

    border: 1px solid transparent;

    background-color: rgb(var(--v-theme-surface));

    padding: 2.2rem 1.6rem;

    border-color: var(--color);
    color: var(--color);
    svg {
      fill: var(--color);
      stroke: var(--color);
    }

    &.success {
      --color: green;
    }

    &.warning {
      --color: orange;
    }

    &.error {
      --color: red;
    }

    &-icon {
      width: 1.8rem;
      aspect-ratio: 1/1;
      // Never squeezed by a long message beside it.
      flex: 0 0 auto;
      margin-block-start: 0.1rem;
    }

    &-close {
      // Pushed to the far edge so it reads as the toast's close, not part of the message.
      margin-inline-start: auto;
      margin-block: -0.6rem 0;
      margin-inline-end: -0.6rem;
      flex: 0 0 auto;

      display: inline-flex;
      align-items: center;
      justify-content: center;
      // A comfortable touch target on the till's touchscreen.
      inline-size: 2.4rem;
      block-size: 2.4rem;

      border: 0;
      border-radius: 50%;
      background: transparent;
      color: var(--color);
      cursor: pointer;

      &:hover,
      &:focus-visible {
        background-color: rgba(var(--v-theme-on-surface), 0.08);
      }

      &:focus-visible {
        outline: 2px solid var(--color);
        outline-offset: 1px;
      }
    }

    &-text {
      font-size: 1.2rem;
      font-weight: 500;
      // `pre-line` keeps the line breaks the backend put between separate reasons; the wrapping
      // rules make sure a long document name breaks instead of forcing the toast wider.
      white-space: pre-line;
      overflow-wrap: anywhere;
      line-height: 1.5;
    }
  }
}
</style>
